<template>
  <div
    v-if="data && status !== 'error'"
    class="comparisonValue"
  >
    <SectionTitle :title="`${pointNameJa}と日本の気温比較`" />
    <div class="table">
      <div class="head">
        <ul class="tabs">
          <button
            v-for="city in compareCityList"
            :key="city"
            class="tab"
            :class="{ 'is-active': selectedCompareCity === city }"
            :data-tab="city"
            @click="selectedCompareCity = city"
          >
            {{ compareCityLabel[city] }}
          </button>
        </ul>
      </div>
      <div class="content">
        <div class="inner">
          <ul class="options">
            <button
              v-for="content in optionList"
              :key="content.key"
              class="option"
              :class="{ 'is-active': selectedOption === content.key }"
              :data-option="content.key"
              @click="selectedOption = content.key"
            >
              {{ content.title }}
            </button>
          </ul>
          <p class="period">
            集計期間： {{ data.point[0].fullDate }} ～ {{ data.point[data.point.length - 1].fullDate }}
          </p>
        </div>
        <div class="legend">
          <p class="current">
            {{ pointNameJa }}
          </p>
          <p>{{ compareCityLabel[selectedCompareCity] }}</p>
        </div>
        <figure class="graph">
          <PointTempChart
            :data="data"
            :tmp-key="selectedOption"
          />
        </figure>
        <!-- 服装・傘アイコン -->
        <div class="clothes">
          <!-- 国旗 (先頭列) -->
          <div class="column">
            <div class="country">
              <figure>
                <img
                  :src="`${runtimeConfig.public.gvsUrl}/onebox/img/world/flag/${cc}.webp`"
                  width="25"
                  height="25"
                  loading="lazy"
                  alt=""
                >
              </figure>
            </div>
            <div class="country">
              <figure>
                <img
                  :src="`${runtimeConfig.public.gvsUrl}/onebox/img/world/flag/jp.webp`"
                  width="25"
                  height="25"
                  loading="lazy"
                  alt=""
                >
              </figure>
            </div>
          </div>
          <!-- 服装 (中身) -->
          <div
            v-for="index in 7"
            :key="index"
            class="column"
          >
            <div class="icon">
              <figure
                :data-tooltip="getClothIconTelop(getClothIconRank(data.point[index - 1]))"
              >
                <img
                  :src="`${runtimeConfig.public.gvsUrl}/onebox/img/world/icon/tops_${getClothIconImgRank(data.point[index - 1])}.svg`"
                  width="33"
                  height="28"
                  loading="lazy"
                  alt=""
                >
              </figure>
            </div>
            <div class="icon">
              <figure
                :data-tooltip="getClothIconTelop(getClothIconRank(data.compare[index - 1]))"
              >
                <img
                  :src="`${runtimeConfig.public.gvsUrl}/onebox/img/world/icon/tops_${getClothIconImgRank(data.compare[index - 1])}.svg`"
                  width="33"
                  height="28"
                  loading="lazy"
                  alt=""
                >
              </figure>
            </div>
          </div>
        </div>
        <p
          class="comment__legend"
          @click="$emit('open-modal')"
        >
          服装アイコンの説明
        </p>
        <!-- 一言コメント -->
        <div
          v-if="graphComment"
          class="comment"
          :class="{ 'is-loading': status === 'pending' }"
          @click="accordion"
        >
          <div
            ref="comment__inner"
            class="comment__inner"
            :data-accordion="accordionStatus"
          >
            <p
              ref="comment__text"
              class="comment__text"
            >
              {{ graphComment }}
            </p>
            <div class="button">
              <span />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import ss1PointList from '~/data/table/ss1_point_list.json'

const props = defineProps({
  pointNameJa: {
    type: String,
    required: true
  },
  pointId: {
    type: String,
    required: true
  },
  now: {
    type: Number,
    required: true
  },
  cc: {
    type: String,
    required: true
  }
})
defineEmits(['open-modal'])
const runtimeConfig = useRuntimeConfig()

const selectedCompareCity = ref('tokyo')
const accordionStatus = ref(false)

const compareCityList = [
  'sapporo',
  'sendai',
  'kanazawa',
  'tokyo',
  'nagoya',
  'osaka',
  'hiroshima',
  'takamatsu',
  'fukuoka',
  'naha'
]
const compareCityLabel = {
  sapporo: '札幌',
  sendai: '仙台',
  kanazawa: '金沢',
  tokyo: '東京',
  nagoya: '名古屋',
  osaka: '大阪',
  hiroshima: '広島',
  takamatsu: '高松',
  fukuoka: '福岡',
  naha: '那覇'
}

const commentInnerRef = useTemplateRef('comment__inner')
const commentTextRef = useTemplateRef('comment__text')
const selectedOption = ref('maxt')
const optionList = [
  {
    title: '最高気温',
    key: 'maxt'
  },
  {
    title: '最低気温',
    key: 'mint'
  }
]

const { data, status } = await useAsyncData(
  `PointMonthlyTemp:${props.pointId}:${selectedCompareCity.value}`,
  async () => {
    const pointDataTask = $fetch(`/api/point/weather?pointId=${props.pointId}`)
    const compareDataTask = $fetch(`/api/ss1/weather?pointId=${selectedCompareCity.value}`)
    // 服装データは補助情報。取得失敗(クライアント再取得時の CORS/referer 制約や
    // バックエンド停止など)でも比較グラフ本体は描画したいので、失敗時は null にして
    // formatCompareData 側の calcClothTemp 推定フォールバックに委ねる。
    const japanClothDataTask = $fetch(`${runtimeConfig.public.siteJpUrl}/api/clothes/pinpoint`, {
      headers: {
        referer: runtimeConfig.public.hostDomain
      },
      params: {
        lat: ss1PointList[selectedCompareCity.value].lat,
        lon: ss1PointList[selectedCompareCity.value].lon
      }
    }).catch(() => null)

    const [pointData, compareData, japanClothData] = await Promise.all([pointDataTask, compareDataTask, japanClothDataTask])
    return {
      // 再取得中は前回の data が残るため、コメントの都市名もデータ側に揃える
      compareCity: selectedCompareCity.value,
      point: formatCompareData(pointData[props.pointId], props.now),
      compare: formatCompareData(compareData[selectedCompareCity.value], props.now, japanClothData, pointData[props.pointId].timezone)
    }
  }, {
    watch: [selectedCompareCity]
  }
)

// 詳細を開いていた場合の調整 (コメント DOM は切り替え中も残るので inline の height も戻す)
watch(selectedCompareCity, () => {
  accordionStatus.value = false
  if (commentInnerRef.value) commentInnerRef.value.style.height = ''
})

const graphComment = computed(() => {
  if (!data.value) return ''
  return getGraphComment(data.value.point, data.value.compare, props.pointNameJa, compareCityLabel[data.value.compareCity])
})

const accordion = () => {
  // 再取得中に開くと旧本文の高さが inline に入り、取得後の本文が長いと末尾が隠れる
  if (status.value === 'pending') return
  if (accordionStatus.value) {
    commentInnerRef.value.style.height = '5.5em'
  } else {
    const height = commentTextRef.value.clientHeight
    commentInnerRef.value.style.height = height + 'px'
  }
  accordionStatus.value = !accordionStatus.value
}
const getClothIconRank = (targetData) => {
  const dayRank = targetData.daytimeClothRank
  const nightRank = targetData.nighttimeClothRank
  let rank = null
  if (dayRank == null && nightRank == null) {
    rank = null
  } else if (dayRank == null) {
    rank = nightRank
  } else if (nightRank == null) {
    rank = dayRank
  } else if (selectedOption.value === 'maxt') {
    rank = Math.max(dayRank, nightRank)
  } else {
    rank = Math.min(dayRank, nightRank)
  }
  return rank
}
const getClothIconImgRank = (target) => {
  const rank = getClothIconRank(target)
  if (rank == null) return 'null'
  return rank.toString().padStart(2, '0')
}
</script>

<style lang="scss" scoped>
.comparisonValue {
  display: flex;
  flex-direction: column;
  gap: 10px;
  .table {
    border: 2px solid #c6ccdc;
  }
  .head {
    display: flex;
    height: 47px;
    .tabs {
      display: flex;
      flex: 1;
      overflow-y: scroll;
      &::-webkit-scrollbar {
        display: none;
      }
      .tab {
        display: flex;
        align-items: center;
        justify-content: center;
        flex: 1;
        min-width: 50px;
        background-color: #eef1f8;
        color: #3569c0;
        text-align: center;
        font-size: 14px;
        line-height: 14px;
        border-bottom: 2px solid #c6ccdc;
        &:not(:last-child) {
          border-right: 1px solid #c6ccdc;
        }
        &.is-active {
          position: relative;
          color: #303030;
          font-weight: 600;
          background-color: #fff;
          &::after {
            position: absolute;
            bottom: -2px;
            left: 0;
            width: 100%;
            height: 2px;
            background-color: #fff;
            content: '';
          }

        }
      }
    }
    .items {
      display: flex;
      padding: 5px;
      gap: 5px;
      background-color: #eef1f8;
      box-shadow: -4px 0 4px 0 rgb(109 124 163 / .25);
      border-bottom: 2px solid #c6ccdc;
      .button {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 35px;
        height: 35px;
        border-radius: 6px;
        border: 1px solid #c0c0c0;
        background-color: #fff;
        box-shadow: 0 1px 2px 0 rgb(0 0 0 / .25);
      }
      .plainImage {
        width: 22px;
        height: 22px;
      }
    }
  }
  .form {
    .search {
      position: relative;
    }
    .input {
      width: 100%;
      background-color: #fff;
      border-radius: 4px;
      padding-left: 10px;
      padding-right: 40px;
      color: #303030;
      font-size: 14px;
      font-weight: 600;
      line-height: 35px
    }
    .submit {
      top: 10px;
      right: 10px;
      transform: translateY(-50%);
      position: absolute;
    }
  }
  .content {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 10px;
    .inner {
      display: flex;
      flex-direction: column;
      gap: 10px;
      flex-direction: row;
      justify-content: space-between;
      .options {
        display: flex;
        justify-content: center;
        padding: 3px;
        border-radius: 6px;
        background-color: #EEF1F8;
      }
      .option {
        padding-inline: 8px;
        color: #3569C0;
        font-size: 12px;
        font-weight: 400;
        line-height: 14px;
        border-right: 1px solid #c6ccdc;
        border-right: none;
        padding: 4px 10px 3px;
        &.is-active {
          color: #fff;
          border-radius: 4px;
          background-color: #3569C0;
        }
      }
      .period {
        flex: 1;
        text-align: right;
        color: #606060;
        font-size: 10px;
        font-weight: 600;
        line-height: 12px;
        span {
          display: block;
        }
      }
    }
    .legend {
      margin-left: auto;
      display: flex;
      padding-left: 5px;
      padding-right: 5px;
      gap: 10px;
      background-color: #fff;
      pointer-events: none;
      p {
        position: relative;
        padding-left: 18px;
        color: #000;
        font-size: 12px;
        font-weight: 600;
        line-height: 13px;
        &::before {
          position: absolute;
          top: 0%;
          left: 0;
          width: 13px;
          height: 13px;
          background-color: #ABCF4E;
          content: '';
        }
        &.current::before {
          background-color: #1E9E08;
        }
      }
    }
  }
  .only-main {
    display: none;
  }
  .clothes {
    display: flex;
    .column {
      &:has(.country) {
        width: 43px;
        border-top: solid 1px transparent;
        border-bottom: solid 1px transparent;
      }
      &:has(.icon) {
        position: relative;
        flex: 1;
      }
      &:nth-child(2n + 2) {
        background-color: #F4F4F4;
        border-top: solid 1px #F4F4F4;
        border-bottom: solid 1px #F4F4F4;
        &::before {
          position: absolute;
          top: -10px;
          left: 50%;
          margin-left: -6px;
          width: 12px;
          height: 10px;
          clip-path: polygon(50% 0, 100% 100%, 0 100%);
          background-color: #F4F4F4;
          content: '';
        }
      }
      &:nth-child(2n + 3) {
        background-color: #fff;
        border-top: solid 1px #F4F4F4;
        border-bottom: solid 1px #F4F4F4;
        &::before {
          position: absolute;
          top: -10px;
          left: 50%;
          margin-left: -6px;
          width: 12px;
          height: 10px;
          clip-path: polygon(50% 0, 100% 100%, 0 100%);
          background-color: #F4F4F4;
          content: '';
        }
        &::after {
          position: absolute;
          top: -9px;
          left: 50%;
          margin-left: -6px;
          width: 12px;
          height: 10px;
          clip-path: polygon(50% 0, 100% 100%, 0 100%);
          background-color: #fff;
          content: '';
        }
      }
    }
    .country {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 35px;
      figure {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 27px;
        height: 27px;
        border: 1px solid #ccc;
        border-radius: 50%;
        overflow: hidden;
        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
      }
    }
    .icon {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 35px;
      figure {
        position: relative;
        height: 28px;
        img {
          width: auto;
          height: 100%;
        }
        // data-tooltip が無い(服装ランク null)図には吹き出しを出さない
        &[data-tooltip]::after {
          position: absolute;
          top: -135%;
          left: 0;
          display: block;
          padding: 7px 8px 5px;
          background: #000;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 300;
          color: #fff;
          white-space: nowrap;
          content: attr(data-tooltip);
          opacity: 0;
          z-index: 10;
          transition: opacity .2s ease-in-out;
          pointer-events: none;
        }
        &[data-tooltip]:hover::after,
        &[data-tooltip]:focus::after,
        &[data-tooltip]:active::after {
          opacity: 1;
        }
      }
    }
    .column:nth-last-child(-n + 4) {
      .icon {
        figure {
          &::after {
            left: auto;
            right: 0;
          }
        }
      }
    }
  }
  .comment {
    position: relative;
    background-color: #F4F4F4;
    padding: 5px 15px 5px 5px;
    transition: opacity .2s ease-in-out;
    &.is-loading {
      opacity: .5;
    }
    &__inner {
      height: 5.5em;
      overflow: hidden;
      pointer-events: none;
      transition: height .2s ease-in-out;
    }
    p {
      color: #000;
      font-size: 15px;
      font-weight: 300;
      line-height: 1.5em;
      text-indent: 1em;
      background-image: url('~/assets/images/common/icon_balloon.png');
      background-position: left top;
      background-size: 1em;
      background-repeat: no-repeat;
      user-select: none;
    }
    .button {
      position: absolute;
      right: 0;
      bottom: 0;
      width: 25px;
      height: 25px;
      padding: 5px;
      background-color: #F4F4F4;
      span {
        position: relative;
        display: block;
        width: 15px;
        height: 15px;
        border-radius: 50%;
        background-color: #1F59BA;
        &::before,
        &::after {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 8px;
          height: 2px;
          background-color: #fff;
          content: '';
        }
        &::after {
          transform: translate(-50%, -50%) rotate(90deg);
          transition: transform .2s ease-in-out;
        }
        &:is([data-accordion='true'] *) {
          &::after {
            transform: translate(-50%, -50%) rotate(0deg);
          }
        }
      }
    }
    &__legend {
      margin-left: auto;
      padding-left: 15px;
      background-image: url('~/assets/images/common/icon_info.svg');
      background-position: left center;
      background-repeat: no-repeat;
      background-size: 12px;
      color: #303030;
      font-size: 12px;
      line-height: 1em;
    }
  }
  @media screen and (min-width: 830px) {
    .head {
      .items {
        .button {
          transition: opacity .2s ease-in-out;
          &:hover {
            opacity: .6;
          }
        }
      }
      .tabs {
        .tab {
          &:not(.is-active) {
            cursor: pointer;
            transition: background-color .2s ease-in-out;
            &:hover {
              // background-color: rgb(67 143 236 / .2);
              background-color: rgb(67 143 236 / .2);
            }
          }
        }
      }
    }
    .content {
      .inner {
        .option:not(.is-active) {
          cursor: pointer;
          &:hover {
            text-decoration: underline;
          }
        }
      }
    }
    .comment {
      &__legend {
        cursor: pointer;
        transition: opacity .2s ease-in-out;
        &:hover {
          opacity: .6;
        }
      }
    }
  }
}
.main {
  .comparisonValue {
    @media screen and (min-width: 830px) {
      .head {
        .items {
          padding: 6px 10px;
          box-shadow: none;
          border-left: 1px solid #c6ccdc;
        }
        .tabs {
          .tab {
            &:last-child {
              border-right: none;
            }
          }
        }
      }
      .content {
        padding: 15px;
        .inner {
          flex-direction: row;
          justify-content: space-between;
          .option {
            font-size: 15px;
            line-height: 16px;
          }
          .period {
            flex: 1;
            font-size: 12px;
            text-align: right;
            span {
              display: inline-block;
            }
          }
        }
      }
      .clothes {
        .column {
          &:has(.country) {
            width: min(3.9vw, 40px);
          }
        }
      }
      .only-main {
        display: block;
        & + .button {
          display: none;
        }
      }
      .comment {
        pointer-events: none;
        padding: 5px;
        &__inner {
          height: 100%;
          .button {
            display: none;
          }
        }
      }
    }
  }
}
</style>
